import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Tv, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',      color: '#E50914' },
  hulu:           { name: 'Hulu',         color: '#1CE783' },
  disney_plus:    { name: 'Disney+',      color: '#113CCF' },
  hbo_max:        { name: 'HBO Max',      color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',    color: '#aaaaaa' },
  peacock:        { name: 'Peacock',      color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',   color: '#0064FF' },
  espn_plus:      { name: 'ESPN+',        color: '#FF0000' },
  crunchyroll:    { name: 'Crunchyroll',  color: '#F47521' },
};

export default function Suggest() {
  const { suggestionId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [done, setDone] = useState(null); // 'added' | 'dismissed'

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/suggest/${suggestionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuggestion(res.data);
      } catch (err) {
        console.error('Failed to load suggestion:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [suggestionId, getToken]);

  const respond = async (confirmed) => {
    setResponding(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API}/suggest/${suggestionId}/respond`,
        { confirmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone(confirmed ? 'added' : 'dismissed');
      if (confirmed && res.data.show_id) {
        setTimeout(() => navigate('/watchlist'), 1500);
      } else {
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err) {
      console.error('Failed to respond:', err);
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508' }}>
        <div style={{ width: 24, height: 24, border: '2px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!suggestion) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508', flexDirection: 'column', gap: 12, padding: 24 }}>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }}>Suggestion not found or already responded to.</p>
        <button onClick={() => navigate('/dashboard')}
          style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)', border: 'none', borderRadius: 20, padding: '10px 24px', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          Go to Dashboard
        </button>
      </div>
    );
  }

  const svc = SERVICE_META[suggestion.service_id] || {};

  if (done) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508', flexDirection: 'column', gap: 16, padding: 24 }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: done === 'added' ? 'linear-gradient(135deg, #22c55e, #4A9EFF)' : 'rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {done === 'added' ? <Check size={32} style={{ color: '#fff' }} /> : <X size={32} style={{ color: '#fff' }} />}
        </div>
        <p style={{ color: '#fff', fontSize: 18, fontWeight: 800, textAlign: 'center' }}>
          {done === 'added' ? `${suggestion.title} added to your watchlist!` : 'Got it — we\'ll learn from that.'}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Taking you back...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508', padding: 24 }}>
      <div style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>

        {/* Show poster */}
        {suggestion.image_url ? (
          <img src={suggestion.image_url} alt={suggestion.title}
            style={{ width: 140, height: 200, borderRadius: 16, objectFit: 'cover', margin: '0 auto 20px', display: 'block', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
        ) : (
          <div style={{ width: 140, height: 200, borderRadius: 16, background: '#1a1a1a', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tv size={40} style={{ color: '#444' }} />
          </div>
        )}

        {/* Service badge */}
        {svc.name && (
          <div style={{
            display: 'inline-block', fontSize: 11, fontWeight: 700, marginBottom: 10,
            color: svc.color, background: `${svc.color}20`,
            border: `1px solid ${svc.color}40`, borderRadius: 20, padding: '3px 12px',
          }}>
            {svc.name}
          </div>
        )}

        <h1 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 8px', lineHeight: 1.2 }}>
          {suggestion.title}
        </h1>

        {suggestion.season && suggestion.episode && (
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', margin: '0 0 8px' }}>
            Season {suggestion.season} · Episode {suggestion.episode}
          </p>
        )}

        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', margin: '0 0 32px', lineHeight: 1.6 }}>
          Qude detected you watching this. Want to add it to your watchlist?
        </p>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => respond(false)}
            disabled={responding}
            style={{
              flex: 1, padding: '14px', borderRadius: 16,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
            <X size={16} /> Not me
          </button>
          <button
            onClick={() => respond(true)}
            disabled={responding}
            style={{
              flex: 2, padding: '14px', borderRadius: 16,
              background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)',
              border: 'none',
              color: '#fff', fontSize: 15, fontWeight: 800,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: responding ? 0.7 : 1,
            }}>
            <Check size={16} /> Yes, add it!
          </button>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
